import { Link, useLocation } from 'react-router-dom'
import {
  BellOutlined,
  DashboardOutlined,
  CloudServerOutlined,
  CommentOutlined,
  RadarChartOutlined,
  SafetyOutlined,
  SolutionOutlined,
  StopOutlined,
  UserOutlined,
  ShoppingOutlined,
  ShopOutlined,
  TeamOutlined,
  FileProtectOutlined,
  KeyOutlined,
  EuroOutlined,
  GlobalOutlined,
  SafetyCertificateOutlined,
  HistoryOutlined,
  ApiOutlined,
  DatabaseOutlined,
} from '@ant-design/icons'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'

import { NC_VERSION } from '../../constants/version'
import { useBranding } from '../../stores/branding'
import apiClient from '../../api/client'

interface PortalSidebarProps {
  isOpen: boolean
  onClose: () => void
}

// A nav item gated by `feature` only shows when the customer actually has a
// service of that category (flags from GET /portal/features).
type PortalFeature = 'dns' | 'ips' | 'usage' | 'monitoring' | 'domains' | 'ssl' | 'licenses' | 'servers' | 'antispam' | 'reseller'

interface NavItem {
  path: string
  labelKey: string
  fallback: string
  icon: ReactNode
  badge?: 'tickets' | 'notifications'
  feature?: PortalFeature
}

interface NavSection {
  labelKey: string
  fallback: string
  items: NavItem[]
}

const SECTIONS: NavSection[] = [
  {
    labelKey: 'portal.sidebar.sections.overview',
    fallback: 'Überblick',
    items: [
      {
        path: '/portal',
        labelKey: 'portal.nav.dashboard',
        fallback: 'Dashboard',
        icon: <DashboardOutlined />,
      },
      {
        path: '/portal/shop',
        labelKey: 'portal.nav.shop',
        fallback: 'Shop',
        icon: <ShoppingOutlined />,
      },
    ],
  },
  {
    labelKey: 'portal.sidebar.sections.services',
    fallback: 'Meine Services',
    items: [
      {
        path: '/portal/services',
        labelKey: 'portal.nav.services',
        fallback: 'Alle Services',
        icon: <CloudServerOutlined />,
      },
      {
        path: '/portal/domains',
        labelKey: 'portal.nav.domains',
        fallback: 'Domains',
        icon: <GlobalOutlined />,
        feature: 'domains',
      },
      {
        path: '/portal/ssl',
        labelKey: 'portal.nav.ssl',
        fallback: 'SSL-Zertifikate',
        icon: <SafetyCertificateOutlined />,
        feature: 'ssl',
      },
      {
        path: '/portal/dns',
        labelKey: 'portal.nav.dns',
        fallback: 'DNS-Zonen',
        icon: <GlobalOutlined />,
        feature: 'dns',
      },
      {
        path: '/portal/licenses',
        labelKey: 'portal.nav.licenses',
        fallback: 'Lizenzen',
        icon: <KeyOutlined />,
        feature: 'licenses',
      },
      {
        path: '/portal/reseller',
        labelKey: 'portal.nav.reseller',
        fallback: 'Reseller',
        icon: <ShopOutlined />,
        feature: 'reseller',
      },
      {
        path: '/portal/servers',
        labelKey: 'portal.nav.servers',
        fallback: 'Meine Server',
        icon: <CloudServerOutlined />,
        feature: 'servers',
      },
      {
        path: '/portal/ips',
        labelKey: 'portal.nav.ips',
        fallback: 'IP-Adressen',
        icon: <DatabaseOutlined />,
        feature: 'ips',
      },
      {
        path: '/portal/usage',
        labelKey: 'portal.nav.usage',
        fallback: 'Datenverbrauch',
        icon: <DatabaseOutlined />,
        feature: 'usage',
      },
      {
        path: '/portal/monitoring',
        labelKey: 'portal.nav.monitoring',
        fallback: 'Monitoring',
        icon: <RadarChartOutlined />,
        feature: 'monitoring',
      },
      {
        path: '/portal/antispam',
        labelKey: 'portal.nav.antispam',
        fallback: 'Antispam',
        icon: <SafetyCertificateOutlined />,
        feature: 'antispam',
      },
    ],
  },
  {
    labelKey: 'portal.sidebar.sections.billing',
    fallback: 'Finanzen',
    items: [
      {
        path: '/portal/billing',
        labelKey: 'portal.nav.billing',
        fallback: 'Finanzen',
        icon: <EuroOutlined />,
      },
    ],
  },
  {
    labelKey: 'portal.sidebar.sections.orders',
    fallback: 'Verträge & Bestellungen',
    items: [
      {
        path: '/portal/contracts',
        labelKey: 'portal.nav.contracts',
        fallback: 'Laufzeitverträge',
        icon: <FileProtectOutlined />,
      },
      {
        path: '/portal/orders',
        labelKey: 'portal.nav.orders',
        fallback: 'Bestellhistorie',
        icon: <HistoryOutlined />,
      },
      {
        path: '/portal/quotes',
        labelKey: 'portal.nav.quotes',
        fallback: 'Angebote',
        icon: <SolutionOutlined />,
      },
      {
        path: '/portal/cancellations',
        labelKey: 'portal.nav.cancellations',
        fallback: 'Kündigungen',
        icon: <StopOutlined />,
      },
    ],
  },
  {
    labelKey: 'portal.sidebar.sections.support',
    fallback: 'Support & Konto',
    items: [
      {
        path: '/portal/tickets',
        labelKey: 'portal.nav.tickets',
        fallback: 'Tickets',
        icon: <CommentOutlined />,
        badge: 'tickets',
      },
      {
        path: '/portal/notifications',
        labelKey: 'portal.nav.notifications',
        fallback: 'Benachrichtigungen',
        icon: <BellOutlined />,
        badge: 'notifications',
      },
      {
        path: '/portal/profile',
        labelKey: 'portal.nav.profile',
        fallback: 'Mein Profil',
        icon: <UserOutlined />,
      },
      {
        path: '/portal/api',
        labelKey: 'portal.nav.api',
        fallback: 'API-Zugang',
        icon: <ApiOutlined />,
      },
      {
        path: '/portal/datenschutz',
        labelKey: 'portal.nav.datenschutz',
        fallback: 'Datenschutz',
        icon: <SafetyOutlined />,
      },
    ],
  },
  {
    labelKey: 'portal.sidebar.sections.partner',
    fallback: 'Partnerprogramm',
    items: [
      {
        path: '/portal/affiliate',
        labelKey: 'portal.nav.affiliate',
        fallback: 'Partnerprogramm',
        icon: <TeamOutlined />,
      },
    ],
  },
]

export default function PortalSidebar({ isOpen, onClose }: PortalSidebarProps) {
  const { t } = useTranslation()
  const location = useLocation()
  const branding = useBranding()

  const openTicketsQ = useQuery({
    queryKey: ['portal-tickets-count-open'],
    queryFn: async () => {
      const res = await apiClient.get<{ data: { count: number } }>(
        '/api/v1/portal/tickets/count-open',
      )
      return res.data.data.count
    },
    refetchInterval: 60_000,
    retry: false,
  })

  const notifCountQ = useQuery({
    queryKey: ['portal-notif-count'],
    queryFn: async () => {
      const r = await apiClient.get<{ data: { unread: number } }>(
        '/api/v1/portal/notifications/count')
      return r.data.data.unread
    },
    refetchInterval: 60_000,
    staleTime: 30_000,
    retry: false,
  })

  const featuresQ = useQuery({
    queryKey: ['portal-features'],
    queryFn: async () => {
      const r = await apiClient.get<{ data: Record<PortalFeature, boolean> }>(
        '/api/v1/portal/features')
      return r.data.data
    },
    staleTime: 5 * 60_000,
    retry: false,
  })

  const hasFeature = (f?: PortalFeature): boolean => {
    if (!f) return true
    if (!featuresQ.data) return false // hide until loaded
    return featuresQ.data[f]
  }

  const isActive = (path: string) => {
    if (path === '/portal') return location.pathname === '/portal'
    return location.pathname.startsWith(path)
  }

  const logoURL = branding.data?.logo_url
  const productName = branding.data?.product_name
    ? `${branding.data.product_name} Portal`
    : t('portal.brand.title', 'NetCell Kundenportal')

  return (
    <nav className={`sidebar${isOpen ? ' open' : ''}`}>
      <div className="sidebar-logo">
        {logoURL
          ? <img src={logoURL} alt={productName} className="sidebar-logo-img" />
          : (<>
              <div className="sidebar-logo-icon">N</div>
              <div className="sidebar-logo-text">{productName}</div>
            </>)}
      </div>

      {SECTIONS.map((section) => {
        const visibleItems = section.items.filter(item => hasFeature(item.feature))
        if (visibleItems.length === 0) return null
        return (
        <div key={section.labelKey}>
          <div className="sidebar-section">
            <div className="sidebar-section-label">
              {t(section.labelKey, section.fallback)}
            </div>
          </div>
          <ul className="sidebar-menu">
            {visibleItems.map((item) => (
              <li
                key={item.path}
                className={`sidebar-menu-item${isActive(item.path) ? ' active' : ''}`}
              >
                <Link to={item.path} onClick={onClose}>
                  {item.icon}
                  <span>{t(item.labelKey, item.fallback)}</span>
                  {item.badge === 'tickets' && (openTicketsQ.data ?? 0) > 0 && (
                    <span className="sidebar-badge">{openTicketsQ.data}</span>
                  )}
                  {item.badge === 'notifications' && (notifCountQ.data ?? 0) > 0 && (
                    <span className="sidebar-badge">{notifCountQ.data}</span>
                  )}
                </Link>
              </li>
            ))}
          </ul>
        </div>
        )
      })}

      <div className="sidebar-version">v{NC_VERSION}</div>
    </nav>
  )
}
